import React, { useState } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Box,
  Typography,
  Chip,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  useTheme,
  Skeleton
} from '@mui/material';
import { LocalShipping, Schedule, Inventory, Warning } from '@mui/icons-material';
import { useRecentShipments, useArrivedShipments, useOverdueShipments } from '../hooks/useDashboardData';

export const ShipmentsOverview: React.FC = () => {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  const { data: upcomingData, isLoading: upcomingLoading } = useRecentShipments(7, 1, 5);
  const { data: arrivedData, isLoading: arrivedLoading } = useArrivedShipments(1, 5);
  const { data: overdueData, isLoading: overdueLoading } = useOverdueShipments(1, 5);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'expected':
        return theme.palette.info.main;
      case 'inreception':
        return theme.palette.warning.main;
      case 'received':
        return theme.palette.success.main;
      case 'analyzing':
        return theme.palette.primary.main;
      default:
        return theme.palette.grey[500];
    }
  };

  return (
    <Card
      sx={{
        height: '100%',
        borderRadius: theme.general.borderRadius,
        boxShadow: theme.colors.shadows.card
      }}
    >
      <CardHeader
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LocalShipping />
            <Typography variant="h4" fontWeight={700}>
              Shipments
            </Typography>
          </Box>
        }
      />
      <CardContent sx={{ pt: 0 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Schedule fontSize="small" />
                <span>Upcoming</span>
                {!upcomingLoading && upcomingData && (
                  <Chip label={upcomingData.totalCount} size="small" sx={{ ml: 0.5, height: 20 }} />
                )}
              </Box>
            }
          />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Inventory fontSize="small" />
                <span>Arrived</span>
                {!arrivedLoading && arrivedData && (
                  <Chip label={arrivedData.totalCount} size="small" sx={{ ml: 0.5, height: 20 }} />
                )}
              </Box>
            }
          />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Warning fontSize="small" />
                <span>Late</span>
                {!overdueLoading && overdueData && overdueData.totalCount > 0 && (
                  <Chip
                    label={overdueData.totalCount}
                    size="small"
                    color="error"
                    sx={{ ml: 0.5, height: 20 }}
                  />
                )}
              </Box>
            }
          />
        </Tabs>

        {/* Upcoming Shipments Tab */}
        {tabValue === 0 && (
          <>
            {upcomingLoading ? (
              <Box>
                {Array.from(new Array(3)).map((_, index) => (
                  <Skeleton key={index} width="100%" height={70} sx={{ mb: 1 }} />
                ))}
              </Box>
            ) : upcomingData && upcomingData.shipments && Array.isArray(upcomingData.shipments) && upcomingData.shipments.length > 0 ? (
              <List dense sx={{ p: 0 }}>
                {upcomingData.shipments.map((shipment) => (
                  <ListItem
                    key={shipment.shipmentId}
                    sx={{
                      backgroundColor: theme.colors.alpha.black[5],
                      borderRadius: theme.general.borderRadiusSm,
                      mb: 1.5,
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      p: 1.5
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        width: '100%',
                        mb: 0.5
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight={700}>
                        {shipment.refNumber}
                      </Typography>
                      <Chip
                        label={shipment.status}
                        size="small"
                        sx={{
                          backgroundColor: getStatusColor(shipment.status),
                          color: '#fff',
                          height: 20
                        }}
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
                      {shipment.supplierName}
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        width: '100%'
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Expected: {new Date(shipment.expectedDate).toLocaleDateString()}
                      </Typography>
                      <Typography variant="caption" fontWeight={600}>
                        {shipment.totalExpectedItems} items
                      </Typography>
                    </Box>
                    {shipment.hasDiscrepancy && (
                      <Chip
                        label="Has Discrepancy"
                        size="small"
                        color="warning"
                        sx={{ mt: 0.5, height: 18 }}
                      />
                    )}
                  </ListItem>
                ))}
                {upcomingData.totalCount > 5 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                    +{upcomingData.totalCount - 5} more upcoming shipments
                  </Typography>
                )}
              </List>
            ) : (
              <Typography color="text.secondary" textAlign="center" sx={{ py: 3 }}>
                No upcoming shipments
              </Typography>
            )}
          </>
        )}

        {/* Arrived Shipments Tab */}
        {tabValue === 1 && (
          <>
            {arrivedLoading ? (
              <Box>
                {Array.from(new Array(3)).map((_, index) => (
                  <Skeleton key={index} width="100%" height={70} sx={{ mb: 1 }} />
                ))}
              </Box>
            ) : arrivedData && arrivedData.shipments && Array.isArray(arrivedData.shipments) && arrivedData.shipments.length > 0 ? (
              <List dense sx={{ p: 0 }}>
                {arrivedData.shipments.map((shipment) => (
                  <ListItem
                    key={shipment.shipmentId}
                    sx={{
                      backgroundColor: theme.colors.alpha.black[5],
                      borderRadius: theme.general.borderRadiusSm,
                      mb: 1.5,
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      p: 1.5
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        width: '100%',
                        mb: 0.5
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight={700}>
                        {shipment.refNumber}
                      </Typography>
                      <Chip
                        label={shipment.status}
                        size="small"
                        sx={{
                          backgroundColor: getStatusColor(shipment.status),
                          color: '#fff',
                          height: 20
                        }}
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
                      {shipment.supplierName}
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        width: '100%'
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        Arrived: {shipment.actualArrivalDate && new Date(shipment.actualArrivalDate).toLocaleDateString()}
                      </Typography>
                      <Typography variant="caption" fontWeight={600}>
                        {shipment.totalReceivedItems} / {shipment.totalExpectedItems} items
                      </Typography>
                    </Box>
                    {shipment.hasDiscrepancy && (
                      <Chip
                        label="Has Discrepancy"
                        size="small"
                        color="warning"
                        sx={{ mt: 0.5, height: 18 }}
                      />
                    )}
                  </ListItem>
                ))}
                {arrivedData.totalCount > 5 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                    +{arrivedData.totalCount - 5} more arrived shipments
                  </Typography>
                )}
              </List>
            ) : (
              <Typography color="text.secondary" textAlign="center" sx={{ py: 3 }}>
                No arrived shipments pending processing
              </Typography>
            )}
          </>
        )}

        {/* Late Shipments Tab */}
        {tabValue === 2 && (
          <>
            {overdueLoading ? (
              <Box>
                {Array.from(new Array(3)).map((_, index) => (
                  <Skeleton key={index} width="100%" height={70} sx={{ mb: 1 }} />
                ))}
              </Box>
            ) : overdueData && overdueData.shipments && Array.isArray(overdueData.shipments) && overdueData.shipments.length > 0 ? (
              <List dense sx={{ p: 0 }}>
                {overdueData.shipments.map((shipment) => (
                  <ListItem
                    key={shipment.shipmentId}
                    sx={{
                      backgroundColor: theme.colors.alpha.error[10],
                      borderRadius: theme.general.borderRadiusSm,
                      border: `1px solid ${theme.colors.alpha.error[30]}`,
                      mb: 1.5,
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      p: 1.5
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        width: '100%',
                        mb: 0.5
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight={700}>
                        {shipment.refNumber}
                      </Typography>
                      <Chip
                        label={`${shipment.daysOverdue} days late`}
                        size="small"
                        color="error"
                        sx={{ height: 20, fontWeight: 600 }}
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
                      {shipment.supplierName}
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        width: '100%'
                      }}
                    >
                      <Typography variant="caption" color="error.main" fontWeight={600}>
                        Expected: {new Date(shipment.expectedDate).toLocaleDateString()}
                      </Typography>
                      <Typography variant="caption" fontWeight={600}>
                        {shipment.totalExpectedItems} items
                      </Typography>
                    </Box>
                  </ListItem>
                ))}
                {overdueData.totalCount > 5 && (
                  <Typography variant="caption" color="error.main" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                    +{overdueData.totalCount - 5} more late shipments
                  </Typography>
                )}
              </List>
            ) : (
              <Typography color="text.secondary" textAlign="center" sx={{ py: 3 }}>
                No late shipments
              </Typography>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
